/**
 * @fileoverview Component for displaying testing portals in a table format.
 * This component includes a search bar, filters, and pagination.
 */

import { ChevronDownIcon, LinkIcon } from '@heroicons/react/20/solid'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Tooltip } from 'react-tooltip'
import CopyIcon from '../../../assets/copy.svg'
import QRIcon from '../../../assets/qr.svg'
import QuestionIcon from '../../../assets/question.svg'
import { useModal } from '../../../hooks/useModal'
import { useToast } from '../../../hooks/useToast'
import {
    useGetInvitesCountForStudyIdQuery,
    useGetInvitesForStudyIdQuery,
} from '../../../services/invites'
import { formatOutput } from '../../../utils/formatOutput'
import { Search } from '../../elements/Search'
import { QRCode } from '../../modals/content/QRCode'
import { Pagination } from '../../Pagination'

/**
 * @function LinkType
 * @description Component for displaying the type of a link.
 * @param {string} type - The type of the link.
 * @param {string} hint - The hint to display.
 * @param {string} id - The ID of the link.
 * @returns {JSX.Element} - A React component that renders the link type.
 */
function LinkType({ type, hint, id }: { type: string; hint: string; id: string }) {
    return (
        <div className="flex gap-x-1 z-0">
            {formatOutput(type)}
            <>
                <QuestionIcon
                    id={`tooltip-${id}`}
                    className="w-5 h-5 fill-gray-400 transition-colors cursor-pointer hover:fill-indigo-500"
                />
                <Tooltip className="z-20" anchorSelect={`#tooltip-${id}`}>
                    <p className="w-40 whitespace-break-spaces text-xs font-normal">{hint}</p>
                </Tooltip>
            </>
        </div>
    )
}

/**
 * @function mapAttributesToType
 * @description Maps the attributes of an invitation to the type of link.
 * @param {Novoic.API.AssignmentInvitation} invite - The invitation to map.
 * @returns {JSX.Element} - A React component that renders the link type.
 */
function mapAttributesToType(invite: Novoic.API.AssignmentInvitation) {
    const length = invite.attributes?.sites?.length | 0

    if (length === 0) {
        return (
            <LinkType
                id={invite.id}
                type="Study-wide"
                hint="Using this link, a test participant will be automatically matched to one of the sites participating in this study based on their location."
            />
        )
    }
    if (length === 1) {
        return (
            <LinkType
                id={invite.id}
                type="Site-specific"
                hint="Using this link, a test participant will be automatically matched to the mentioned site, regardless of their location."
            />
        )
    }
    return (
        <LinkType
            id={invite.id}
            type="Cluster-specific"
            hint="Using this link, a test participant will be automatically matched to one of the sites from the allocated cluster."
        />
    )
}

/**
 * @function TestingProtalTab
 * @description Component for displaying testing portals in a table format.
 * @returns {JSX.Element} - A React component that renders the testing portals.
 */
export function TestingProtalTab() {
    const params = useParams()
    const [openToast] = useToast()
    const [open] = useModal()
    const {
        data: invites,
        isLoading,
        refetch: refetchInvites,
    } = useGetInvitesForStudyIdQuery(params.studyId)
    const { data: total, refetch: refetchInvitesCount } = useGetInvitesCountForStudyIdQuery(
        params.studyId
    )

    const columns = ['Name', 'Type', 'Battery', 'Link']

    function onCopyLink(link: string | undefined) {
        navigator.clipboard.writeText(link)
        openToast({
            title: 'Link copied!',
            icon: <LinkIcon className="h-6 w-6 text-indigo-400" aria-hidden="true" />,
        })
    }

    useEffect(() => {
        refetchInvites()
        refetchInvitesCount()
    }, [location.search])

    if (isLoading) {
        return null
    }

    return (
        <>
            <div className="flex sticky top-[177px] bg-white border-b px-8 py-6 z-10">
                <Search placeholder="Search by link name..." />
            </div>
            <table className="w-full">
                <thead className="nv-thead top-[266px] sticky">
                    <tr className="h-12">
                        {columns.map((col, index) => (
                            <th
                                key={col}
                                scope="col"
                                className={`${
                                    index === 0 ? 'pl-8 pr-2.5' : 'px-2.5'
                                } text-left text-sm font-medium text-gray-900 whitespace-nowrap`}
                            >
                                <button className="group inline-flex">
                                    {col}
                                    <span className="invisible ml-2 flex-none rounded text-gray-400 group-hover:visible group-focus:visible">
                                        <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
                                    </span>
                                </button>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                    {invites.map((invite: Novoic.API.AssignmentInvitation) => (
                        <tr
                            key={invite.id}
                            className="hover:bg-[#f7f9ff] cursor-pointer transition-all"
                        >
                            <td
                                className="pl-8 whitespace-nowrap pr-10 py-4 w-[60px] text-sm font-medium"
                                role="presentation"
                            >
                                {formatOutput(invite.name || 'Primary testing portal')}
                            </td>
                            <td className="px-2.5 whitespace-nowrap pr-10 py-4 w-[60px] text-sm">
                                {mapAttributesToType(invite)}
                            </td>
                            <td className="px-2.5 whitespace-nowrap pr-10 py-4 w-[60px] text-sm">
                                {formatOutput(invite.battery?.name)}
                            </td>
                            <td className="px-2.5 pr-10 py-4 text-sm text-gray-600">
                                <div className="flex whitespace-nowrap w-fit gap-x-1 -ml-2 px-2 py-1 transition-all rounded hover:text-indigo-700 cursor-pointer hover:bg-indigo-100">
                                    <button
                                        className="flex gap-x-1 group"
                                        onClick={(e) => {
                                            e.preventDefault()
                                            e.stopPropagation()
                                            onCopyLink(invite.link)
                                        }}
                                    >
                                        {invite.link}
                                        <CopyIcon className="h-5 group-hover:fill-indigo-700 hover:fill-indigo-700" />
                                    </button>

                                    <QRIcon
                                        onClick={() => {
                                            open({
                                                content: (
                                                    <QRCode
                                                        downloadable
                                                        title={
                                                            invite.name || 'Primary testing portal'
                                                        }
                                                        invitation={invite}
                                                    />
                                                ),
                                            })
                                        }}
                                        className="h-5 hover:fill-indigo-700"
                                    />
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className="flex w-full h-[74px] items-center bg-white border-t border-t-gray-200">
                <Pagination total={total?.count} />
            </div>
        </>
    )
}
